// ============================================
// Manor Miser - Menu Scene (native resolution)
// ============================================

import Phaser from 'phaser';
import { socketManager } from '../network/SocketManager';
import { UIButton } from '../ui/UIButton';
import { UIInput } from '../ui/UIInput';
import { SoundManager } from '../audio/SoundManager';
import { MIN_BOTS, MAX_BOTS, BOT_NAME_LIST } from '../shared/constants';

export class MenuScene extends Phaser.Scene {
  private nameInput!: UIInput;
  private roomCodeInput!: UIInput;
  private roomCodeLabel!: Phaser.GameObjects.BitmapText;
  private errorText!: Phaser.GameObjects.BitmapText;
  private joinMode: boolean = false;
  private createBtn!: UIButton;
  private joinBtn!: UIButton;
  private singlePlayerBtn!: UIButton;
  private botCount: number = 3;
  private botCountText!: Phaser.GameObjects.BitmapText;
  private botCountContainer!: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    this.joinMode = false;

    const { width, height } = this.cameras.main;

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x1a1412, 1);
    bg.fillRect(0, 0, width, height);

    // Title
    const title = this.add.bitmapText(width / 2, 4, 'oldtome', 'Manor Miser');
    title.setOrigin(0.5, 0).setScale(1).setTint(0xd4a855);

    // Subtitle
    const subtitle = this.add.bitmapText(width / 2, 30, 'pixel', 'PAY YOUR TRIBUTE TO THE LORD');
    subtitle.setOrigin(0.5, 0).setScale(1).setTint(0xa08060);

    // Name input label
    const nameLabel = this.add.bitmapText(width / 2, 42, 'pixel', 'ENTER YOUR NAME');
    nameLabel.setOrigin(0.5, 0).setScale(1).setTint(0xd4a855);

    // Name input
    this.nameInput = new UIInput(this, width / 2, 56, 100, 14, 'SIR LANCELOT');

    // Room code section (initially hidden)
    this.roomCodeLabel = this.add.bitmapText(width / 2, 68, 'pixel', 'ROOM CODE');
    this.roomCodeLabel.setOrigin(0.5, 0).setScale(1).setTint(0xd4a855);
    this.roomCodeLabel.setVisible(false);

    this.roomCodeInput = new UIInput(this, width / 2, 82, 70, 14, 'ABCD');
    this.roomCodeInput.setVisible(false);
    this.roomCodeInput.setMaxLength(4);

    // Buttons
    const buttonY = this.joinMode ? 100 : 76;

    this.createBtn = new UIButton(this, width / 2 - 34, buttonY, 'CREATE', () => {
      this.handleCreateRoom();
    }, 60, 16);

    this.joinBtn = new UIButton(this, width / 2 + 34, buttonY, 'JOIN', () => {
      this.handleJoinToggle();
    }, 60, 16);

    // Single Player button
    const spButtonY = buttonY + 22;
    this.singlePlayerBtn = new UIButton(this, width / 2, spButtonY, 'SINGLE PLAYER', () => {
      this.handleSinglePlayer();
    }, 80, 16);

    // Bot count selector
    this.botCountContainer = this.add.container(width / 2, spButtonY + 16);

    const botLeft = this.add.bitmapText(-20, 0, 'pixel', ',');
    botLeft.setOrigin(0.5).setScale(1).setTint(0xd4a855);
    botLeft.setInteractive({ useHandCursor: true });

    this.botCountText = this.add.bitmapText(0, 0, 'pixel', `${this.botCount} BOTS`);
    this.botCountText.setOrigin(0.5).setScale(1).setTint(0xf5e6d3);

    const botRight = this.add.bitmapText(20, 0, 'pixel', '.');
    botRight.setOrigin(0.5).setScale(1).setTint(0xd4a855);
    botRight.setInteractive({ useHandCursor: true });

    botLeft.on('pointerover', () => botLeft.setTint(0xf5e6d3));
    botLeft.on('pointerout', () => botLeft.setTint(0xd4a855));
    botLeft.on('pointerdown', () => {
      SoundManager.init();
      SoundManager.click();
      this.botCount = Math.max(MIN_BOTS, this.botCount - 1);
      this.botCountText.setText(`${this.botCount} BOTS`);
    });

    botRight.on('pointerover', () => botRight.setTint(0xf5e6d3));
    botRight.on('pointerout', () => botRight.setTint(0xd4a855));
    botRight.on('pointerdown', () => {
      SoundManager.init();
      SoundManager.click();
      this.botCount = Math.min(MAX_BOTS, this.botCount + 1);
      this.botCountText.setText(`${this.botCount} BOTS`);
    });

    this.botCountContainer.add([botLeft, this.botCountText, botRight]);

    // Error text
    this.errorText = this.add.bitmapText(width / 2, height - 30, 'pixel', '');
    this.errorText.setOrigin(0.5).setScale(1).setTint(0xff6b6b);

    // Connection status
    this.createConnectionStatus();

    // Sound toggle
    this.createSoundToggle();

    // Fade in
    this.cameras.main.fadeIn(500, 26, 20, 18);

    // Socket callbacks
    this.setupSocketCallbacks();
  }

  private createSoundToggle(): void {
    const { width } = this.cameras.main;

    const isMuted = SoundManager.muted;
    const label = isMuted ? 'M' : 'S';

    const soundBtn = this.add.bitmapText(width - 6, 4, 'pixel', label);
    soundBtn.setOrigin(1, 0).setScale(1).setTint(0xd4a855).setAlpha(0.7);
    soundBtn.setInteractive({ useHandCursor: true });

    soundBtn.on('pointerover', () => soundBtn.setAlpha(1));
    soundBtn.on('pointerout', () => soundBtn.setAlpha(0.7));
    soundBtn.on('pointerdown', () => {
      SoundManager.init();
      const nowMuted = SoundManager.toggleMute();
      soundBtn.setText(nowMuted ? 'M' : 'S');
      if (!nowMuted) {
        SoundManager.click();
      }
    });
  }

  private createConnectionStatus(): void {
    const { width, height } = this.cameras.main;

    const isConnected = socketManager.isConnected;
    const statusLabel = isConnected ? 'CONNECTED' : 'DISCONNECTED';
    const statusColor = isConnected ? 0x6bff6b : 0xff6b6b;

    const status = this.add.bitmapText(width / 2, height - 10, 'pixel', statusLabel);
    status.setOrigin(0.5).setTint(statusColor).setScale(1);

    if (!isConnected) {
      const retryBtn = this.add.bitmapText(width / 2, height - 20, 'pixel', 'CLICK TO RETRY');
      retryBtn.setOrigin(0.5).setTint(0xa08060).setScale(1);
      retryBtn.setInteractive({ useHandCursor: true });

      retryBtn.on('pointerdown', async () => {
        retryBtn.setText('CONNECTING...');
        try {
          await socketManager.connect();
          this.scene.restart();
        } catch (e) {
          retryBtn.setText('CLICK TO RETRY');
        }
      });
    }
  }

  private setupSocketCallbacks(): void {
    socketManager.setCallbacks({
      onError: (message) => {
        this.showError(message);
      },
      onRoomCreated: (roomCode, playerId, player) => {
        this.registry.set('roomCode', roomCode);
        this.registry.set('playerId', playerId);
        this.registry.set('playerName', player.name);
        this.registry.set('isHost', true);
        this.registry.set('players', [player]);
        this.registry.set('hostId', playerId);

        this.cameras.main.fadeOut(300, 26, 20, 18);
        this.time.delayedCall(300, () => {
          this.scene.start('LobbyScene');
        });
      },
      onRoomJoined: (roomCode, playerId, player, players, hostId) => {
        this.registry.set('roomCode', roomCode);
        this.registry.set('playerId', playerId);
        this.registry.set('playerName', player.name);
        this.registry.set('isHost', hostId === playerId);
        this.registry.set('players', players);
        this.registry.set('hostId', hostId);

        this.cameras.main.fadeOut(300, 26, 20, 18);
        this.time.delayedCall(300, () => {
          this.scene.start('LobbyScene');
        });
      },
      onGameStarted: (gameState, yourMoney) => {
        this.registry.set('roomCode', gameState.roomCode);
        this.registry.set('playerId', socketManager.socketId);
        this.registry.set('playerName', this.nameInput.getValue().trim());
        this.registry.set('isHost', true);
        this.registry.set('isSinglePlayer', true);
        this.registry.set('singlePlayerBotCount', this.botCount);
        this.registry.set('players', gameState.players);
        this.registry.set('hostId', gameState.hostId);

        this.cameras.main.fadeOut(300, 26, 20, 18);
        this.time.delayedCall(300, () => {
          this.scene.start('GameScene');
        });
      },
    });
  }

  private getPlayerName(): string {
    const name = this.nameInput.getValue().trim();
    if (name) return name;
    return BOT_NAME_LIST[Math.floor(Math.random() * BOT_NAME_LIST.length)];
  }

  private handleCreateRoom(): void {
    SoundManager.init();
    SoundManager.click();

    const name = this.getPlayerName();

    if (!socketManager.isConnected) {
      this.showError('NOT CONNECTED TO SERVER');
      return;
    }

    this.clearError();
    this.createBtn.setDisabled(true);
    this.createBtn.setText('WAIT...');
    socketManager.createRoom(name);

    this.time.delayedCall(3000, () => {
      this.createBtn.setDisabled(false);
      this.createBtn.setText('CREATE');
    });
  }

  private handleSinglePlayer(): void {
    SoundManager.init();
    SoundManager.click();

    const name = this.getPlayerName();

    this.clearError();
    this.singlePlayerBtn.setDisabled(true);
    this.singlePlayerBtn.setText('STARTING...');
    socketManager.startSinglePlayer(name, this.botCount);

    this.time.delayedCall(3000, () => {
      this.singlePlayerBtn.setDisabled(false);
      this.singlePlayerBtn.setText('SINGLE PLAYER');
    });
  }

  private handleJoinToggle(): void {
    SoundManager.init();
    SoundManager.click();

    if (!this.joinMode) {
      this.joinMode = true;
      this.roomCodeLabel.setVisible(true);
      this.roomCodeInput.setVisible(true);

      this.roomCodeLabel.setAlpha(0);
      this.roomCodeInput.setAlpha(0);

      this.tweens.add({
        targets: [this.roomCodeLabel, this.roomCodeInput],
        alpha: 1,
        duration: 200,
      });

      this.tweens.add({
        targets: [this.createBtn, this.joinBtn],
        y: 100,
        duration: 200,
      });

      this.tweens.add({
        targets: [this.singlePlayerBtn],
        y: 122,
        duration: 200,
      });
      this.tweens.add({
        targets: [this.botCountContainer],
        y: 138,
        duration: 200,
      });

      this.joinBtn.setText('JOIN');
    } else {
      this.handleJoinRoom();
    }
  }

  private handleJoinRoom(): void {
    const name = this.getPlayerName();
    const roomCode = this.roomCodeInput.getValue().trim().toUpperCase();

    if (!roomCode) {
      this.showError('ENTER A ROOM CODE');
      SoundManager.error();
      this.roomCodeInput.focus();
      return;
    }

    if (!socketManager.isConnected) {
      this.showError('NOT CONNECTED');
      return;
    }

    this.clearError();
    this.joinBtn.setDisabled(true);
    this.joinBtn.setText('WAIT...');
    socketManager.joinRoom(roomCode, name);

    this.time.delayedCall(3000, () => {
      this.joinBtn.setDisabled(false);
      this.joinBtn.setText('JOIN');
    });
  }

  private showError(message: string): void {
    this.errorText.setText(message);
    this.errorText.setAlpha(0);

    this.tweens.add({
      targets: this.errorText,
      alpha: 1,
      duration: 200,
    });

    this.tweens.add({
      targets: this.errorText,
      x: this.errorText.x + 2,
      duration: 50,
      yoyo: true,
      repeat: 3,
    });
  }

  private clearError(): void {
    this.errorText.setText('');
  }
}
